import React, { useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';

import application from 'application';

import internalEventCodes from 'event-system/codes/internal';
import externalEventCodes from 'event-system/codes/external';

import { series } from 'utilities';

import StartupRoutes from './startup-routes';

import SystemToastMessage from './system-toast-message';
import AsyncDialogManager from './async-dialog-manager';
import OfflineView from './offline-view';

import './index.scss';

function Root() {

  const [rootState, setRootState] = useState({
    initialized: application.isInitialized(),
    initializing: false,
    error: {
      yes: false,
      message: ''
    },
    displayPoweredByBanner: false
  });

  useEffect(function onLoad() {

    addBrowserClass();
    initializeApplication();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function registerForEvents() {

    const eventRegisterers = [
      registerBannerVisibilityUpdateEventHandler,
      registerServerSystemToastMessageEventHandler
    ];

    const unregisterEventHandlers = series(eventRegisterers);

    return function unregisterEvents() {
      series(unregisterEventHandlers);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addBrowserClass() {
    var userAgent = navigator.userAgent.toLowerCase();
    var browserClass = '';

    if (userAgent.indexOf('chrome') > -1 || userAgent.indexOf('crios') > -1) {
      if (userAgent.indexOf('mobile') > -1) {
        browserClass = 'chrome-mobile';
      } else {
        browserClass = 'chrome-desktop';
      }
    } else if (userAgent.indexOf('safari') > -1) {
      if (userAgent.indexOf('mobile') > -1) {
        browserClass = 'safari-mobile';
      } else {
        browserClass = 'safari-desktop';
      }
    } else if (userAgent.indexOf('firefox') > -1) {
      if (userAgent.indexOf('mobile') > -1) {
        browserClass = 'firefox-mobile';
      } else {
        browserClass = 'firefox-desktop';
      }
    }

    if (browserClass !== '') {
      document.body.classList.add(browserClass);
    }
  }

  function registerBannerVisibilityUpdateEventHandler() {

    function bannerVisibilityUpdateHandler(data) {

      setRootState(function updateRootState(rootState) {

        return {
          ...rootState,
          displayPoweredByBanner: data.display
        };

      });

    }

    application.eventSystem.internal.on(internalEventCodes.UPDATE_POWERED_BY_BANNER_VISIBILITY, bannerVisibilityUpdateHandler);

    return function unregisterServerSystemToastMessageEventHandler() {
      application.eventSystem.internal.off(internalEventCodes.UPDATE_POWERED_BY_BANNER_VISIBILITY, bannerVisibilityUpdateHandler);
    };

  }

  function registerServerSystemToastMessageEventHandler() {

    function connectedToServerEventHandler(data) {

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: data.message,
        seconds: 5,
        type: data.type
      });

    }

    application.eventSystem.internal.on(externalEventCodes.SYSTEM_TOAST_MESSAGE, connectedToServerEventHandler);

    return function unregisterServerSystemToastMessageEventHandler() {
      application.eventSystem.internal.off(externalEventCodes.SYSTEM_TOAST_MESSAGE, connectedToServerEventHandler);
    };

  }

  async function initializeApplication() {

    try {

      setRootState({
        ...rootState,
        initializng: true
      });

      await application.initialize();

      setRootState({
        ...rootState,
        initializng: false,
        initialized: true
      });

    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception);
      setRootState({
        ...rootState,
        error: {
          yes: true,
          message: exception.message
        }
      });

      application.constructor.logError('ERROR_INITIALIZING_APPLICATION', exception);

    }

  }

  function tryRenderApplicationInitializationError() {

    if (rootState.error.yes === false) {
      return;
    }

    const message = rootState.error.message || 'Unknown error. Please contact administration';

    return <div id="application-initialization-error-message-container">
      <div id="application-initialization-error-message">{message}</div>
    </div>;

  }

  function renderSystemToastMessage() {

    return <SystemToastMessage />;

  }

  function renderAsyncDialogManager() {

    return <AsyncDialogManager />;

  }

  function renderRoot() {

    if (rootState.initialized === false) {
      return;
    }

    const properties = {
      startup: application.getCurrentStartup()
    };

    return <StartupRoutes {...properties} />;

  }

  function tryRenderSpinner() {

    if (rootState.initialized === true) {
      return;
    }

    return <div id="root-level-spinner-container">
      <ht-spinner />
    </div>;

  }

  function renderOfflineView() {

    return <OfflineView />;

  }

  function renderPoweredByStartup() {

    if (rootState.displayPoweredByBanner === false) {
      return;
    }

    const startupPlatformLinkAttributes = {
      id: 'startup-platform-link',
      href: 'https://venturz.co',
      target: '_blank'
    };

    return <span id="powered-by-text">
      Powered by <a {...startupPlatformLinkAttributes}>Startup Platform</a>
    </span>;
  }

  return (
    <div id="root-main" className="startup-text-color">
      {renderRoot()}
      {tryRenderApplicationInitializationError()}
      {renderOfflineView()}
      {renderSystemToastMessage()}
      {tryRenderSpinner()}
      {renderAsyncDialogManager()}
      <div id="root-level-dynamic-elements-container"></div>
      <Helmet>
        <meta charSet="utf-8" />
      </Helmet>
    </div>
  );
}

export default Root;